import { useMemo, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Collapsible } from '@opendoor/atoms/collapsible';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { IconChevronDown } from '@tabler/icons-react';
import type {
  MarketplacePropertyListFragment,
  MarketplacePropertySearchFragment,
} from '__generated__/athena';
import { fetchMainSiteHeader } from 'cms';
import { createContentfulClient } from 'cms/api';
import { getAthenaClient } from 'components/api';
import { COSMOS_URL } from 'components/globals';
import { MapMarker } from 'components/marketplace/map/MapMarker';
import { MarketplaceMap } from 'components/marketplace/map/MarketplaceMap';
import type { MapMarkerProps, TopMarker } from 'components/marketplace/map/types';
import { SimilarListings } from 'components/marketplace/property';
import { Schools } from 'components/marketplace/property/Schools';
import MarketplaceMapLayout from 'components/properties/shared/MarketplaceMapLayout';
import { Divider } from 'components/shared/Divider';
import type { ISiteHeader } from 'declarations/contentful';
import type {
  CacheSafeGetServerSideProps,
  InferGetServerSidePropsType,
} from 'declarations/shared/serverSideProps';
import type { MapRef } from 'react-map-gl';

const GuideSection = ({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <Collapsible.Root open={isOpen} onOpenChange={setIsOpen} className="w-full">
      <Collapsible.Trigger className="flex h-26.25 w-full items-center justify-between gap-1 border-0 bg-transparent px-0">
        <Text as="span" className="text-subheader-xs text-left text-od-content-primary">
          {title}
        </Text>
        <IconChevronDown
          size={20}
          className={cn('shrink-0 transition-transform', isOpen && 'rotate-180')}
        />
      </Collapsible.Trigger>
      <Collapsible.Panel className="pb-5.5">{children}</Collapsible.Panel>
    </Collapsible.Root>
  );
};

const Page: React.FC<InferGetServerSidePropsType<typeof getServerSideProps>> = ({
  entry,
  header,
  marketMetrics,
  properties,
  propertiesForMap,
  slug,
}) => {
  const [topMarker, setTopMarker] = useState<TopMarker | null>(null);
  const mapRef = useRef<MapRef>(null);

  const markerRefs = useRef<ReactNode | null>(null);
  const homesForSaleMarkers = useMemo(() => {
    markerRefs.current = propertiesForMap?.results?.map((property, idx) => {
      const handleClick = () => {
        window.open(
          `${COSMOS_URL}/properties/${property?.slug ?? '_'}/aid_${
            property?.addressToken
          }?utm_source=neighborhood-guide&utm_term=${slug}`,
        );
      };

      const lat = property.location?.lat;
      if (lat === undefined) {
        return undefined;
      }

      const lon = property.location?.lon;
      if (lon === undefined) {
        return undefined;
      }

      const mergedProperty = {
        ...property,
        addressToken: property.addressToken || '',
        isLiked: false,
        location: {
          lat,
          lon,
        },
      };

      return (
        <MapMarker
          key={`${property.addressToken}`}
          property={mergedProperty}
          onClick={handleClick}
          moveToTop={(hasFocus: boolean) => {
            setTopMarker({ index: idx, address: property.addressToken || '', hasFocus });
          }}
          setFocused={topMarker?.hasFocus}
        />
      );
    });
    return markerRefs.current;
  }, [propertiesForMap, properties]);

  return (
    <MarketplaceMapLayout header={header}>
      <Box className="flex-row-reverse bg-od-bg-primary w-full overflow-hidden h-screen max-md:flex-col">
        <Col className="w-full h-full overflow-y-scroll pb-20 px-12 md:basis-1/2">
          <Text as="h2" className="text-label-md text-od-content-tertiary pt-5">
            Neighborhood Guide
          </Text>
          <Text as="h1" className="text-header-md pt-2.5">
            {entry.fields.neighborhood}
          </Text>
          <Text as="h2" className="text-body-sm pt-7.5">
            {entry.fields.overview}
          </Text>
          <Row className="justify-between max-lg:flex-col">
            {entry.fields.pros_cons.pros.length > 0 && (
              <Col className="w-[48%] max-lg:w-full">
                <Text as="p" className="text-label-md pt-6 pb-3">
                  What to love
                </Text>
                <Col className="pl-4">
                  {entry.fields.pros_cons.pros.map((pro: string) => (
                    <Row key={pro} className="gap-2">
                      <Text as="span" className="text-body-sm">
                        •
                      </Text>
                      <Text as="p" className="text-body-sm">
                        {pro}
                      </Text>
                    </Row>
                  ))}
                </Col>
              </Col>
            )}
            {entry.fields.pros_cons.cons.length > 0 && (
              <Col className="w-[48%] max-lg:w-full">
                <Text as="p" className="text-label-md pt-6 pb-3">
                  What to consider
                </Text>
                <Col className="pl-4">
                  {entry.fields.pros_cons.cons.map((con: string) => (
                    <Row key={con} className="gap-2">
                      <Text as="span" className="text-body-sm">
                        •
                      </Text>
                      <Text as="p" className="text-body-sm">
                        {con}
                      </Text>
                    </Row>
                  ))}
                </Col>
              </Col>
            )}
          </Row>
          {/** Market Trends */}
          <Divider className="pt-12 pb-4" />
          <GuideSection title="Market Trends" defaultOpen>
            <Col className="gap-6">
              <Text as="p" className="text-body-sm">
                {entry.fields.real_estate_market_trends}
              </Text>
              <Col className="bg-od-bg-secondary p-6 rounded-2xl">
                <Row className="justify-between">
                  <Text as="p" className="text-header-md text-od-content-primary">
                    $
                    {marketMetrics?.medianSalePrice.toLocaleString('en-US', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: marketMetrics?.medianSalePrice >= 1_000_000 ? 2 : 0,
                      notation: 'compact',
                      compactDisplay: 'short',
                    })}
                  </Text>
                  <Text as="span" className="text-label-sm text-od-content-primary">
                    {marketMetrics?.medianSalePriceMom && marketMetrics?.medianSalePriceMom < 0
                      ? '↘'
                      : '↗'}{' '}
                    {Math.round(marketMetrics?.medianSalePriceMom * 100)}%
                  </Text>
                </Row>
                <Text as="p" className="text-label-sm text-od-content-secondary pt-3">
                  Median sale price
                </Text>
              </Col>
              <Row className="justify-between">
                <Col className="bg-od-bg-secondary p-6 rounded-2xl w-[48%]">
                  <Row className="justify-between">
                    <Text as="p" className="text-header-md text-od-content-primary">
                      {Math.round(marketMetrics?.daysOnMarket)}
                    </Text>
                    <Text as="span" className="text-label-sm text-od-content-primary">
                      {marketMetrics?.daysOnMarketMom && marketMetrics?.daysOnMarketMom < 0
                        ? '↘'
                        : '↗'}{' '}
                      {Math.round(marketMetrics?.daysOnMarketMom * 100)}%
                    </Text>
                  </Row>
                  <Text as="p" className="text-label-sm text-od-content-secondary pt-3">
                    Average days on market
                  </Text>
                </Col>
                <Col className="bg-od-bg-secondary p-6 rounded-2xl w-[48%]">
                  <Row className="justify-between">
                    <Text as="p" className="text-header-md text-od-content-primary">
                      {marketMetrics?.homesSold}
                    </Text>
                    <Text as="span" className="text-label-sm text-od-content-primary">
                      {marketMetrics?.homesSoldMom && marketMetrics?.homesSoldMom < 0 ? '↘' : '↗'}{' '}
                      {Math.round(marketMetrics?.homesSoldMom * 100)}%
                    </Text>
                  </Row>
                  <Text as="p" className="text-label-sm text-od-content-secondary pt-3">
                    Homes sold
                  </Text>
                </Col>
              </Row>
              <Text as="p" className="text-label-xs text-od-content-inactive-primary pt-6">
                Market trends are based on the previous 4 weeks of data in the{' '}
                {entry.fields.neighborhood_zip_code} zip code.
              </Text>
            </Col>
          </GuideSection>
          <Divider className="py-4" />
          <Schools
            schools={entry.fields.school_data}
            summary={entry.fields.schools_and_education}
            disclaimer="Data provided by GreatSchools"
          />
          <Divider className="py-4" />
          {/** Safety */}
          <GuideSection title="Safety">
            <Col>
              <Text as="p" className="text-body-sm">
                {entry.fields.safety}
              </Text>
              <Row className="justify-between pt-6">
                <Text as="p" className="text-body-sm text-od-content-primary">
                  Overall Crime Risk
                </Text>
                <Text as="p" className="text-label-sm text-od-content-primary">
                  {entry.fields.crime_data?.overall_crime
                    ? `${entry.fields.crime_data.overall_crime}`
                    : 'N/A'}
                </Text>
              </Row>
              <Divider className="py-4" />
              <Row className="justify-between">
                <Text as="p" className="text-body-sm text-od-content-primary">
                  Violent Crime Risk
                </Text>
                <Text as="p" className="text-label-sm text-od-content-primary">
                  {entry.fields.crime_data?.violent_crime
                    ? `${entry.fields.crime_data.violent_crime}`
                    : 'N/A'}
                </Text>
              </Row>
              <Divider className="py-4" />
              <Row className="justify-between">
                <Text as="p" className="text-body-sm text-od-content-primary">
                  Property Crime Risk
                </Text>
                <Text as="p" className="text-label-sm text-od-content-primary">
                  {entry.fields.crime_data?.property_crime
                    ? `${entry.fields.crime_data.property_crime}`
                    : 'N/A'}
                </Text>
              </Row>
              <Text as="p" className="text-label-xs text-od-content-inactive-primary pt-6">
                Provided by Ovogo.com
              </Text>
            </Col>
          </GuideSection>
          <Divider className="py-4" />
          <GuideSection title="Transportation">
            <Col>
              <Text as="p" className="text-body-sm">
                {entry.fields.transportation}
              </Text>
              <Col className="pt-6">
                {Object.keys(entry.fields.public_transit)?.map((category: string, index) => (
                  <Col key={category}>
                    <Col
                      className={cn(
                        'justify-between',
                        index === Object.keys(entry.fields.public_transit).length - 1
                          ? 'pb-6'
                          : 'pb-0',
                      )}
                    >
                      <Text as="p" className="text-body-sm text-od-content-secondary capitalize">
                        {category.split('_').join(' ')}
                      </Text>
                      <Text as="p" className="text-body-sm text-od-content-primary">
                        {entry.fields.public_transit?.[category]}
                      </Text>
                    </Col>
                    {index !== Object.keys(entry.fields.public_transit).length - 1 && (
                      <Divider className="py-4" />
                    )}
                  </Col>
                ))}
              </Col>
            </Col>
          </GuideSection>
          <Divider className="py-4" />
          <GuideSection title="Entertainment and recreation">
            <Col>
              <Text as="p" className="text-body-sm">
                {entry.fields.entertainment_and_recreation}
              </Text>
              <br />
              <Text as="p" className="text-body-sm">
                {entry.fields.community_events}
              </Text>
            </Col>
          </GuideSection>
          <Divider className="py-4" />
          <GuideSection title="Parks">
            <Col>
              <Text as="p" className="text-body-sm">
                {entry.fields.parks_and_green_spaces}
              </Text>
            </Col>
          </GuideSection>
          <Divider className="py-4" />
          <Col className="pt-8.5">
            <SimilarListings
              listings={properties?.results || []}
              title={`Available homes near ${entry.fields.neighborhood}`}
              utmSource="neighborhood-guide"
              utmTerm={slug as string}
            />
          </Col>
        </Col>
        <Col className="h-screen max-md:hidden md:basis-1/2 md:overflow-y-scroll">
          <MarketplaceMap
            mapRef={mapRef}
            handleOnClick={() => {}}
            handleOnMapMove={() => {}}
            center={[entry.fields.location?.lat, entry.fields.location?.lon]}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            mapMarkers={homesForSaleMarkers as ReactElement<MapMarkerProps>[]}
            zoom={11}
            dragPan={false}
            showGeoLocationControls={false}
          />
        </Col>
      </Box>
    </MarketplaceMapLayout>
  );
};

type ContentfulEntry = {
  fields: {
    location: {
      lat: number;
      lon: number;
    };
    neighborhood: string;
    overview: string;
    pros_cons: {
      pros: string[];
      cons: string[];
    };
    real_estate_market_trends: string;
    schools_and_education: string;
    safety: string;
    crime_data: {
      overall_crime: string;
      violent_crime: string;
      property_crime: string;
    };
    transportation: string;
    public_transit: { [k: string]: string };
    entertainment_and_recreation: string;
    restaurants_and_shopping: string;
    community_events: string;
    local_businesses: string;
    parks_and_green_spaces: string;
    tips_for_home_buyers: string;
    school_data: {
      name: string;
      latitude: number;
      longitude: number;
    }[];
    neighborhood_zip_code: string;
  };
};

type ContentfulEntries = {
  items: ContentfulEntry[];
};

export const getServerSideProps: CacheSafeGetServerSideProps<{
  entry: ContentfulEntry;
  header: ISiteHeader | null;
  properties?: MarketplacePropertyListFragment | null;
  propertiesForMap?: MarketplacePropertySearchFragment | null;
  marketMetrics: any;
  slug: string | string[] | undefined;
}> = async (context) => {
  const SEARCH_RADIUS_METERS = 4000;
  const TRENDS_LOOKBACK_WEEKS = 4;

  const slug = context.query.slug;
  if (!slug) {
    return {
      notFound: true,
    };
  }
  const client = createContentfulClient();

  const [entry, header] = await Promise.all([
    client.getEntries({
      content_type: 'neighborhoodGuide',
      include: 3,
      'fields.slug': slug,
      limit: 1,
    }) as Promise<ContentfulEntries>,
    fetchMainSiteHeader().catch(() => null),
  ]);

  if (!entry?.items?.length) {
    return {
      notFound: true,
    };
  }

  const nearbyZones = await getAthenaClient(context.req.headers).SearchZonesByLocation({
    latitude: entry.items[0].fields.location.lat,
    longitude: entry.items[0].fields.location.lon,
    searchDistance: SEARCH_RADIUS_METERS,
  });
  const zoneIds = nearbyZones?.geoDataStore?.searchZonesByLocation?.zones.map((zone) => zone.uuid);

  const propertyList = await getAthenaClient().GetMarketplacePropertyList({ page: 0, zoneIds });
  const propertySearch = await getAthenaClient().GetMarketplacePropertySearch({ zoneIds });
  const marketMetrics = await getAthenaClient().HistoricalMarketMetricsQuery({
    zip: entry.items[0].fields.neighborhood_zip_code,
    numWeeks: TRENDS_LOOKBACK_WEEKS,
  });

  return {
    props: {
      entry: entry.items[0],
      header,
      properties: propertyList?.marketplace?.propertyList,
      propertiesForMap: propertySearch?.marketplace?.propertySearch,
      marketMetrics: marketMetrics?.sellerDashboardMetrics?.getHistoricalMarketMetrics,
      slug,
    },
  };
};

Page.displayName = 'NeighborhoodGuidePage';
export default Page;
